import React, { FunctionComponent } from "react";
import { getDefaultStyleProperty } from "@iventis/map-engine/src/utilities/style-helpers";
import { Content } from "@iventis/translations/content/typed-content";
import { LineModelStyle } from "@iventis/domain-model/model/lineModelStyle";
import {
    colourSelectorComponentCreator,
    ColourSelectorFormulaPreview,
    DashedLineSvgComponent,
    definedValueSelectorComponentCreator,
    DottedLineSvgComponent,
    incrementalValueSelectorComponentCreator,
    LargeDashLineSvgComponent,
    LineStyleSVGComponent,
    selectItemsComponentCreator,
    SmallDashLineSvgComponent,
    StraightLineSvgComponent,
} from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { dataTestIds } from "@iventis/testing";
import { getStaticStyleValue } from "@iventis/layer-style-helpers";
import { LineType } from "@iventis/domain-model/model/lineType";
import { EditStyleItemComponent } from "./edit-style-item";
import { EditStyleProps } from "./style.types";
import BaseModelEditStyleComponent from "./base-model-edit-style";
import StyleContainer from "./style-container";
import { StyledStyleItemContainer } from "./line-edit-style";

export const LineModelEditStyleComponent: FunctionComponent<EditStyleProps<LineModelStyle>> = ({ layerStyle, changeStyleValue, dataFields }) => {
    const translate = useIventisTranslate();
    return (
        <>
            <BaseModelEditStyleComponent layerStyle={layerStyle} dataFields={dataFields} changeStyleValue={changeStyleValue}>
                {/* ROTATION INCREMENTOR */}
                <EditStyleItemComponent
                    styleProperty="rotation"
                    value={layerStyle.rotation ?? getDefaultStyleProperty(layerStyle.styleType, "rotation")}
                    changeStyleValue={changeStyleValue}
                    component={incrementalValueSelectorComponentCreator({
                        minValue: 0,
                        maxValue: 359,
                        increment: 10,
                        units: [{ id: "degrees", name: "degrees" }],
                        selectedUnitId: "degrees",
                        testId: "line-model-rotation",
                    })}
                    title={translate(Content.map2.styles.rotation)}
                    isZoomableValue={false}
                />
                {/* MODEL SPACING */}
                <EditStyleItemComponent
                    styleProperty="spacing"
                    value={layerStyle.spacing || getDefaultStyleProperty(layerStyle.styleType, "spacing")}
                    changeStyleValue={changeStyleValue}
                    component={incrementalValueSelectorComponentCreator({
                        minValue: 0.1,
                        maxValue: Infinity,
                        increment: 0.1,
                        units: [{ id: "m", name: "metres" }],
                        selectedUnitId: "m",
                        decimals: 2,
                        testId: "line-model-spacing",
                    })}
                    title={translate(Content.map2.styles2.spacing)}
                    isZoomableValue={false}
                    dataFields={dataFields}
                    showDataDriven
                />
                {/* MODEL OFFSET */}
                <EditStyleItemComponent
                    styleProperty="modelOffset"
                    value={layerStyle.modelOffset || getDefaultStyleProperty(layerStyle.styleType, "modelOffset")}
                    changeStyleValue={changeStyleValue}
                    component={incrementalValueSelectorComponentCreator({
                        minValue: -Infinity,
                        maxValue: Infinity,
                        increment: 0.1,
                        units: [{ id: "m", name: "metres" }],
                        selectedUnitId: "m",
                        decimals: 2,
                        testId: "line-model-offset",
                    })}
                    title={translate(Content.map7.styles.modelOffset)}
                    isZoomableValue={false}
                    dataFields={dataFields}
                    showDataDriven
                />
            </BaseModelEditStyleComponent>
            <StyleContainer title={translate(Content.map2.styles2.Line)} icon={<LineStyleSVGComponent />} testId="line" defaultOpen={false}>
                <StyledStyleItemContainer>
                    {/* LINE TOGGLE */}
                    <EditStyleItemComponent
                        styleProperty="lineModelShowLine"
                        value={layerStyle.lineModelShowLine ?? getDefaultStyleProperty(layerStyle.styleType, "lineModelShowLine")}
                        component={definedValueSelectorComponentCreator<boolean>({
                            optionalValues: [
                                { value: true, label: translate(Content.map2.styles2.on), testId: "line-toggle-on" },
                                { value: false, label: translate(Content.map2.styles2.off), testId: "line-toggle-off" },
                            ],
                        })}
                        changeStyleValue={changeStyleValue}
                        title={translate(Content.map2.styles2.Line)}
                    />
                    {/* COLOUR SELECTOR */}
                    <EditStyleItemComponent
                        styleProperty="lineModelLineColour"
                        value={layerStyle.lineModelLineColour || getDefaultStyleProperty(layerStyle.styleType, "lineModelLineColour")}
                        changeStyleValue={changeStyleValue}
                        component={colourSelectorComponentCreator()}
                        PreviewComponent={ColourSelectorFormulaPreview}
                        showDataDriven
                        dataFields={dataFields}
                        title={translate(Content.map2.styles.line_model_line_colour)}
                        disabled={!getStaticStyleValue(layerStyle.lineModelShowLine)}
                    />
                    {/* WIDTH INCREMENTOR */}
                    <EditStyleItemComponent
                        styleProperty="lineModelLineWidth"
                        value={layerStyle.lineModelLineWidth || getDefaultStyleProperty(layerStyle.styleType, "lineModelLineWidth")}
                        changeStyleValue={changeStyleValue}
                        component={incrementalValueSelectorComponentCreator({
                            minValue: 0,
                            maxValue: 20,
                            increment: 1,
                            units: [{ id: "px", name: translate(Content.map3.units.pixels) }],
                            selectedUnitId: "px",
                            testId: dataTestIds.editLayer.lineWidth,
                        })}
                        title={translate(Content.map2.styles.line_model_line_width)}
                        showHiddenOptions
                        disabled={!getStaticStyleValue(layerStyle.lineModelShowLine)}
                    />
                    {/* TYPE OF LINE */}
                    <EditStyleItemComponent
                        styleProperty="lineModelLineType"
                        component={selectItemsComponentCreator({
                            options: [
                                { id: LineType.Solid, name: translate(Content.map2.styles2.solid), component: StraightLineSvgComponent },
                                { id: LineType.LargeDash, name: translate(Content.map11.styles.lineTypes.largeDash), component: LargeDashLineSvgComponent },
                                { id: LineType.Dashed, name: translate(Content.map2.styles2.dashed), component: DashedLineSvgComponent },
                                { id: LineType.SmallDash, name: translate(Content.map11.styles.lineTypes.smallDash), component: SmallDashLineSvgComponent },
                                { id: LineType.Dotted, name: translate(Content.map11.styles.lineTypes.dotted), component: DottedLineSvgComponent },
                            ],
                            verticalPadding: 0,
                            testId: "line-type-selector",
                        })}
                        value={layerStyle.lineModelLineType ?? getDefaultStyleProperty(layerStyle.styleType, "lineModelLineType")}
                        changeStyleValue={changeStyleValue}
                        title={translate(Content.map2.styles2.dashed)}
                        disabled={!getStaticStyleValue(layerStyle.lineModelShowLine)}
                        showDataDriven
                        dataFields={dataFields}
                    />
                    {/* OUTLINE ON/OFF */}
                    <EditStyleItemComponent
                        styleProperty="lineModelLineOutline"
                        component={definedValueSelectorComponentCreator<boolean>({
                            optionalValues: [
                                { value: true, label: translate(Content.map2.styles.outline) },
                                { value: false, label: translate(Content.common.none), testId: "outline-none" },
                            ],
                        })}
                        value={layerStyle.lineModelLineOutline ?? getDefaultStyleProperty(layerStyle.styleType, "lineModelLineOutline")}
                        changeStyleValue={changeStyleValue}
                        title={translate(Content.map2.styles.outline)}
                        disabled={!getStaticStyleValue(layerStyle.lineModelShowLine)}
                    />
                    {/* OUTLINE COLOUR */}
                    <EditStyleItemComponent
                        styleProperty="lineModelLineOutlineColour"
                        component={colourSelectorComponentCreator({ testId: "layer-style-outline-colour-selector" })}
                        value={layerStyle.lineModelLineOutlineColour ?? getDefaultStyleProperty(layerStyle.styleType, "lineModelLineOutlineColour")}
                        changeStyleValue={changeStyleValue}
                        title={translate(Content.map2.styles.outline_colour)}
                        disabled={!getStaticStyleValue(layerStyle.lineModelShowLine) || !getStaticStyleValue(layerStyle.lineModelLineOutline)}
                    />
                    {/* OUTLINE WIDTH */}
                    <EditStyleItemComponent
                        styleProperty="lineModelLineOutlineWidth"
                        value={layerStyle.lineModelLineOutlineWidth ?? getDefaultStyleProperty(layerStyle.styleType, "lineModelLineOutlineWidth")}
                        component={incrementalValueSelectorComponentCreator({
                            minValue: 0,
                            maxValue: 10,
                            increment: 1,
                            units: [{ id: "px", name: translate(Content.map3.units.pixels) }],
                            selectedUnitId: "px",
                            testId: "layer-style-outline-width",
                        })}
                        changeStyleValue={changeStyleValue}
                        title={translate(Content.map2.styles.outline_width)}
                        disabled={!getStaticStyleValue(layerStyle.lineModelShowLine) || !getStaticStyleValue(layerStyle.lineModelLineOutline)}
                    />
                </StyledStyleItemContainer>
            </StyleContainer>
        </>
    );
};
